import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider } from '@material-ui/core';
import { Button, LoadingBounce, WithDialogsProps, useDialogs } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { Customer, auth } from 'Auth';
import { Contact, ContactLocationRelationship } from 'components/Contact/utils';
import Messages from 'components/Messages';
import { Column } from 'devextreme-react/data-grid';
import { ContactControlDelegator, ContactDetails, DependencyConflict } from '../..';
import {
  ExternalLevelDeassignation,
  PRIMARY_LEVEL_VALUE,
  SECONDARY_LEVEL_VALUE,
  TIRE_SERVICE_VALUE,
  mutateExternalContactsUnassign,
} from '../../utils';
import ManageContactTypesLevelsTable from '../tables/ManageContactTypesLevelsTable';
import ReassignSelectedRoleAreasTable from '../tables/ReassignSelectedRoleAreasTable';
import { isRoleAreaServiceLevelMichelinOffer, reassignPriorityLevelRender } from '../tables/utils';
import { buildContactTypesForNewContact, contactRelationshipsQuery } from '../utils';

interface Props {
  contact: Contact;
  open: boolean;
  handleClose: (
    save: boolean,
    contactSelectedReassignRoleAreaLocation: Map<string, ReassignSelection>,
    unassignLocalRoleLocationComboSet: Set<string>,
  ) => void;
  alreadySelected: Map<string, ReassignSelection>;
  locationsFilters: Set<string>;
  specificRoleAreaToReassign?: string;
  specificServiceToReassign?: string;
  secondaryDependencies: Array<DependencyConflict> | undefined;
}

interface QueryState {
  errors: any;
  loading: boolean;
  locationContacts: Array<Contact>;
}

export interface ReassignSelection {
  contact: Contact;
  service: string;
  roleArea: string;
  location: string;
  level: string;
}

export default function ReassignContactTypesDialog(props: Props): JSX.Element {
  const {
    contact,
    open,
    handleClose,
    alreadySelected,
    locationsFilters,
    specificRoleAreaToReassign,
    specificServiceToReassign,
    secondaryDependencies,
  } = props;
  const { t } = useTranslation();
  const [selectedRoleAreaLocationComboToReassign, setSelectedRoleAreaLocationComboToReassign] = useState(
    new Set<string>(),
  );
  const [selectedRoleAreasByLocation, setSelectedRoleAreasByLocation] = useState(
    new Map<string, Array<{ roleArea: string; level: string }>>(),
  );
  // For mandatory Michelin Offers
  const mandatoryRoleAreaLocationComboToReassign = new Set<string>();
  const mandatoryRoleAreasByLocation = new Map<string, Array<{ roleArea: string; level: string }>>();
  // For specific Role/Area Reassign (When remove only one Role/Area with a Priority Level assigned)
  const specificRoleAreaLocationComboToReassign = new Set<string>();
  const specificRoleAreasByLocation = new Map<string, Array<{ roleArea: string; level: string }>>();
  const [locationIndex, setLocationIndex] = useState(0);
  const [secondStep, setSecondStep] = useState(false);
  const [queryState, setQueryState] = useState<QueryState>({
    loading: true,
    errors: undefined,
    locationContacts: [],
  });
  const [contactSelectedReassignRoleAreaLocation, setContactSelReaRoleLocation] = useState(new Map(alreadySelected));
  const [michelinOffersPendingAssign, setMichelinOffersPendingAssign] = useState(new Set());
  const [mandatorySecondaryConflictsPendingAssign, setMandatorySecondaryConflictsPendingAssign] = useState(new Set());
  const [createContactMode, setCreateContactMode] = useState(false);
  const [blockedIndexes, setBlockedIndexes] = useState(new Map<number, Contact>());
  const [alreadyShownWarningCreate, setAlreadyShownWarningCreate] = useState(false);
  const dialogs: WithDialogsProps = useDialogs();
  const [unassignLocalRoleLocationComboSet, setUnassignLocalRoleLocationComboSet] = useState(new Set<string>());
  const locationsMap = new Map<string, Customer>();
  const { loading, errors, locationContacts } = queryState;
  const mandatorySecondaryConflictsSet = new Set<string>();
  const selectedService = specificServiceToReassign || TIRE_SERVICE_VALUE;
  const dialogTitle = t('Manage Contact Type Priority Levels at Locations');
  const contactController = useRef<ContactControlDelegator>({});

  if (secondaryDependencies) {
    secondaryDependencies.forEach((x) => {
      mandatorySecondaryConflictsSet.add(x.service + x.role_area + x.location_hash_key);
    });
  }

  // Specifc mandatory reassign.
  if (specificRoleAreaToReassign) {
    const { contact_types } = contact;
    if (contact_types) {
      contact_types.forEach((contactType) => {
        const { role_areas, service } = contactType;
        if (role_areas && (service || TIRE_SERVICE_VALUE) === selectedService) {
          role_areas.forEach((contactTypeRoleArea) => {
            const { levels, role_area } = contactTypeRoleArea;

            if (role_area === specificRoleAreaToReassign) {
              levels.forEach((contactTypeLevel) => {
                const { level, location } = contactTypeLevel;
                if (locationsFilters.size === 0 || locationsFilters.has(location)) {
                  if (isRoleAreaServiceLevelMichelinOffer(role_area, selectedService, level)) {
                    mandatoryRoleAreaLocationComboToReassign.add(role_area + location);
                    mandatoryRoleAreasByLocation.set(location, [
                      {
                        roleArea: role_area,
                        level,
                      },
                    ]);
                  } else if (mandatorySecondaryConflictsSet.has(`${selectedService}${role_area}${location}`)) {
                    mandatoryRoleAreaLocationComboToReassign.add(role_area + location);
                    mandatoryRoleAreasByLocation.set(location, [
                      {
                        roleArea: role_area,
                        level,
                      },
                    ]);
                  }

                  specificRoleAreaLocationComboToReassign.add(role_area + location);
                  const roleAreasByLocation = specificRoleAreasByLocation.get(location);
                  if (roleAreasByLocation) {
                    roleAreasByLocation.push({
                      roleArea: role_area,
                      level,
                    });
                    specificRoleAreasByLocation.set(location, roleAreasByLocation);
                  } else {
                    specificRoleAreasByLocation.set(location, [
                      {
                        roleArea: role_area,
                        level,
                      },
                    ]);
                  }
                }
              });
            }
          });
        }
      });
    }
  }

  // Setup locations map
  if (contact.locations) {
    contact.locations
      .filter((x) => x.is_deleted === false)
      .forEach((contactLocationRelationship) => {
        if (contactLocationRelationship.location) {
          locationsMap.set(contactLocationRelationship.location.hash_key, contactLocationRelationship.location);
        }
      });
  }

  // Only show Contact Type with Tire Service
  const contactType =
    contact.contact_types && contact.contact_types.find((x) => (x.service || TIRE_SERVICE_VALUE) === selectedService);

  // Process contact types per location
  const contactTypePerLocation = new Map();
  if (contactType && contactType.role_areas) {
    contactType.role_areas.forEach((y) => {
      y.levels.forEach((z) => {
        const alreadyAdded = contactTypePerLocation.get(z.location);
        if (alreadyAdded) {
          alreadyAdded.push({
            roleArea: y.role_area,
            level: z.level,
          });
        } else {
          contactTypePerLocation.set(z.location, [
            {
              roleArea: y.role_area,
              level: z.level,
            },
          ]);
        }
      });
    });
  }

  // Maintain Michelin offers selected
  if (contactType && specificRoleAreaToReassign === undefined) {
    const { role_areas, service } = contactType;
    if (role_areas && (service || TIRE_SERVICE_VALUE) === selectedService) {
      role_areas.forEach((contactTypeRoleArea) => {
        const { role_area, levels } = contactTypeRoleArea;
        levels.forEach((contactTypeLevel) => {
          const { location, level } = contactTypeLevel;
          if (locationsFilters.size === 0 || locationsFilters.has(location)) {
            if (level === PRIMARY_LEVEL_VALUE || level === SECONDARY_LEVEL_VALUE) {
              mandatoryRoleAreaLocationComboToReassign.add(role_area + location);
              const roleAreasByLocation = mandatoryRoleAreasByLocation.get(location);
              if (roleAreasByLocation) {
                if (!roleAreasByLocation.some((x) => x.roleArea === role_area)) {
                  roleAreasByLocation.push({
                    roleArea: role_area,
                    level,
                  });
                }
                mandatoryRoleAreasByLocation.set(location, roleAreasByLocation);
              } else {
                mandatoryRoleAreasByLocation.set(location, [
                  {
                    roleArea: role_area,
                    level,
                  },
                ]);
              }
            } else if (mandatorySecondaryConflictsSet.has(`${selectedService}${role_area}${location}`)) {
              mandatoryRoleAreaLocationComboToReassign.add(role_area + location);
              mandatoryRoleAreasByLocation.set(location, [
                {
                  roleArea: role_area,
                  level,
                },
              ]);
            }
          }
        });
      });
    }
  }

  // Query External Contacts
  useEffect(() => {
    const asyncQuery = async () => {
      setQueryState({
        loading: true,
        errors: undefined,
        locationContacts: [],
      });

      const { apolloClient } = auth;
      if (apolloClient) {
        if (contact.hash_key) {
          const locationsKeys = new Set(Array.from(selectedRoleAreasByLocation.keys()));
          Array.from(mandatoryRoleAreasByLocation.keys()).forEach((x) => {
            locationsKeys.add(x);
          });
          Array.from(specificRoleAreasByLocation.keys()).forEach((x) => {
            locationsKeys.add(x);
          });
          const locationsHashKeys = Array.from(locationsKeys.values());
          const selectedLocationHashKey = locationsHashKeys[locationIndex];
          const selectedLocation = locationsMap.get(selectedLocationHashKey);

          const queryResponse = await apolloClient.query({
            query: contactRelationshipsQuery,
            fetchPolicy: 'no-cache',
            variables: { locations: selectedLocation ? [selectedLocation.customer_number] : [] },
          });

          const { data, errors } = queryResponse;

          if (errors && errors.length > 0) {
            setQueryState({
              loading: false,
              errors,
              locationContacts: [],
            });
          } else if (typeof data !== 'undefined' && data !== null) {
            const { getContactLocationsRelationships } = data;
            const locationContacts = getContactLocationsRelationships
              .filter(
                (x: ContactLocationRelationship) =>
                  x.contact && x.contact.hash_key !== contact.hash_key && x.is_deleted === false,
              )
              .map((x: ContactLocationRelationship) => x.contact);
            setQueryState({
              loading: false,
              errors: undefined,
              locationContacts,
            });
          } else {
            setQueryState({
              loading: false,
              locationContacts: [],
              errors: undefined,
            });
          }
        }
      }
    };
    asyncQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.locations, locationIndex, secondStep, open]);

  const handleCheck = (checked: boolean, role_area: string, location: string, level: string) => {
    const roleAreasInLocations = selectedRoleAreasByLocation.get(location);
    if (checked) {
      if (roleAreasInLocations) {
        roleAreasInLocations.push({ roleArea: role_area, level });
        selectedRoleAreasByLocation.set(location, roleAreasInLocations);
      } else {
        selectedRoleAreasByLocation.set(location, [{ roleArea: role_area, level }]);
      }
      selectedRoleAreaLocationComboToReassign.add(role_area + location);
    } else {
      if (roleAreasInLocations) {
        const indexToDelete = roleAreasInLocations.findIndex((x) => x.roleArea === role_area);
        roleAreasInLocations.splice(indexToDelete, 1);
        if (roleAreasInLocations.length === 0) {
          selectedRoleAreasByLocation.delete(location);
        } else {
          selectedRoleAreasByLocation.set(location, roleAreasInLocations);
        }
      }
      selectedRoleAreaLocationComboToReassign.delete(role_area + location);
    }
    setSelectedRoleAreaLocationComboToReassign(new Set(selectedRoleAreaLocationComboToReassign));
    setSelectedRoleAreasByLocation(new Map(selectedRoleAreasByLocation));
  };

  const onClose = (save: boolean) => {
    handleClose(save, contactSelectedReassignRoleAreaLocation, unassignLocalRoleLocationComboSet);
    setSelectedRoleAreaLocationComboToReassign(new Set());
    setSelectedRoleAreasByLocation(new Map());
    setLocationIndex(0);
    setSecondStep(false);
    setQueryState({
      loading: false,
      errors: undefined,
      locationContacts: [],
    });
    setContactSelReaRoleLocation(new Map());
    setCreateContactMode(false);
    setAlreadyShownWarningCreate(false);
    setBlockedIndexes(new Map());
    setUnassignLocalRoleLocationComboSet(new Set());
    setMichelinOffersPendingAssign(new Set());
    setMandatorySecondaryConflictsPendingAssign(new Set());
  };

  const mandatorySelectedSpecificRoleLocComboToReassign = new Set(selectedRoleAreaLocationComboToReassign);
  Array.from(mandatoryRoleAreaLocationComboToReassign.values()).forEach((x) => {
    mandatorySelectedSpecificRoleLocComboToReassign.add(x);
  });

  Array.from(specificRoleAreaLocationComboToReassign.values()).forEach((x) => {
    mandatorySelectedSpecificRoleLocComboToReassign.add(x);
  });

  const requiredReassignments = new Set<string>();
  const addRequiredReassignments = (
    location: string,
    roleAreas: Array<{ roleArea: string; level: string }> | undefined,
  ) => {
    roleAreas?.forEach(({ roleArea, level }) => {
      requiredReassignments.add(`${selectedService}|${roleArea}|${String(location)}|${level}`);
    });
  };

  selectedRoleAreasByLocation.forEach((roleAreas, location) => addRequiredReassignments(location, roleAreas));
  mandatoryRoleAreasByLocation.forEach((roleAreas, location) => addRequiredReassignments(location, roleAreas));
  specificRoleAreasByLocation.forEach((roleAreas, location) => addRequiredReassignments(location, roleAreas));

  const selectedReassignmentKeys = new Set(
    Array.from(contactSelectedReassignRoleAreaLocation.values())
      .filter((selection) => (selection.service || TIRE_SERVICE_VALUE) === selectedService)
      .map((selection) => `${selectedService}|${selection.roleArea}|${String(selection.location)}|${selection.level}`),
  );

  let content = (
    <ManageContactTypesLevelsTable
      viewMode={false}
      contact={contact}
      contactTypePerLocation={contactTypePerLocation}
      contactType={contactType}
      locationsFilters={locationsFilters}
      actionColumns={[
        <Column
          width="140px"
          caption={t('Reassign Priority Level(s)')}
          dataField="reassign"
          alignment="center"
          headerCellRender={() => <p style={{ whiteSpace: 'pre-wrap' }}>{t('Reassign Priority Level(s)')}</p>}
          cellRender={(row: any) =>
            reassignPriorityLevelRender(row.data, mandatorySelectedSpecificRoleLocComboToReassign, handleCheck)
          }
          allowResizing
          allowSorting={false}
          buttons
        />,
      ]}
    />
  );

  let secondActionButton = (
    <Button
      disabled={mandatorySelectedSpecificRoleLocComboToReassign.size === 0 || secondStep}
      color="primary"
      onClick={() => {
        setSecondStep(true);
      }}
    >
      {t('Continue')}
    </Button>
  );

  if (secondStep || specificRoleAreaToReassign !== undefined) {
    if (loading) {
      content = <LoadingBounce />;
    } else if (errors && errors.length > 0) {
      content = <Messages.PlainMessage title="" messages={['', '']} />;
    } else {
      const locationsKeys = new Set(Array.from(selectedRoleAreasByLocation.keys()));
      Array.from(mandatoryRoleAreasByLocation.keys()).forEach((x) => {
        locationsKeys.add(x);
      });
      Array.from(specificRoleAreasByLocation.keys()).forEach((x) => {
        locationsKeys.add(x);
      });
      const locationsHashKeys = Array.from(locationsKeys.values());
      const selectedLocationHashKey = locationsHashKeys[locationIndex];
      const selectedLocation = locationsMap.get(selectedLocationHashKey);
      const selectedRoleAreasInLocation = selectedRoleAreasByLocation.get(selectedLocationHashKey);

      // Map to avoid repeating roleArea
      const mappedSelectedRoleAreas = new Map<string, { level: string; roleArea: string }>();
      if (selectedRoleAreasInLocation) {
        selectedRoleAreasInLocation.forEach((x) => {
          mappedSelectedRoleAreas.set(x.level + x.roleArea, x);
        });
      }

      const mandatoryRoleAreas = mandatoryRoleAreasByLocation.get(selectedLocationHashKey);
      const specificRoleAreas = specificRoleAreasByLocation.get(selectedLocationHashKey);

      if (mandatoryRoleAreas) {
        mandatoryRoleAreas.forEach((x) => {
          mappedSelectedRoleAreas.set(x.level + x.roleArea, x);
        });
      }

      if (specificRoleAreas) {
        specificRoleAreas.forEach((x) => {
          mappedSelectedRoleAreas.set(x.level + x.roleArea, x);
        });
      }

      const selectedRoleAreas = Array.from(mappedSelectedRoleAreas.values());

      if (selectedLocation && selectedRoleAreas && selectedRoleAreas.length > 0) {
        const checkHandler = (
          value: boolean,
          externalContact: Contact,
          roleArea: string,
          level: string,
          location: string,
        ) => {
          if (value) {
            // Contact selected
            contactSelectedReassignRoleAreaLocation.set(`${selectedService}${roleArea}${location}${level}`, {
              contact: externalContact,
              service: selectedService,
              level,
              roleArea,
              location,
            });

            if (isRoleAreaServiceLevelMichelinOffer(roleArea, selectedService, level)) {
              michelinOffersPendingAssign.add(roleArea + level + selectedLocationHashKey);
            } else if (mandatorySecondaryConflictsSet.has(`${selectedService}${roleArea}${location}`)) {
              mandatorySecondaryConflictsPendingAssign.add(`${selectedService}${roleArea}${location}`);
            }

            setContactSelReaRoleLocation(new Map(contactSelectedReassignRoleAreaLocation));
          } else {
            // Contact removed
            contactSelectedReassignRoleAreaLocation.delete(`${selectedService}${roleArea}${location}${level}`);

            if (isRoleAreaServiceLevelMichelinOffer(roleArea, selectedService, level)) {
              michelinOffersPendingAssign.delete(roleArea + level + selectedLocationHashKey);
            } else if (mandatorySecondaryConflictsSet.has(`${selectedService}${roleArea}${location}`)) {
              mandatorySecondaryConflictsPendingAssign.delete(`${selectedService}${roleArea}${location}`);
            }

            setContactSelReaRoleLocation(new Map(contactSelectedReassignRoleAreaLocation));
          }
          setMichelinOffersPendingAssign(new Set(michelinOffersPendingAssign));
          setMandatorySecondaryConflictsPendingAssign(new Set(mandatorySecondaryConflictsPendingAssign));
        };

        const { customer_name, extrnl_cust_id, customer_addr1, customer_addr2 } = selectedLocation;
        if (createContactMode) {
          const contactTypes = buildContactTypesForNewContact(selectedRoleAreas, selectedLocation, selectedService);
          const onAfterSaveHandler = (newContactCreate: Contact) => {
            // Update contact types in local Contact
            const roleAreaLocationComboToDeassignate = new Set<string>();
            if (selectedRoleAreas) {
              selectedRoleAreas.forEach((roleAreaToUnassign) => {
                roleAreaLocationComboToDeassignate.add(
                  `${selectedService}${roleAreaToUnassign.roleArea}${selectedLocationHashKey}`,
                );
                unassignLocalRoleLocationComboSet.add(
                  `${selectedService}${roleAreaToUnassign.roleArea}${selectedLocationHashKey}`,
                );
                if (
                  isRoleAreaServiceLevelMichelinOffer(
                    roleAreaToUnassign.roleArea,
                    selectedService,
                    roleAreaToUnassign.level,
                  )
                ) {
                  michelinOffersPendingAssign.add(
                    roleAreaToUnassign.roleArea + roleAreaToUnassign.level + selectedLocationHashKey,
                  );
                } else if (
                  mandatorySecondaryConflictsSet.has(
                    `${selectedService}${roleAreaToUnassign.roleArea}${selectedLocationHashKey}`,
                  )
                ) {
                  mandatorySecondaryConflictsPendingAssign.add(
                    `${selectedService}${roleAreaToUnassign.roleArea}${selectedLocationHashKey}`,
                  );
                }

                // Contact selected
                contactSelectedReassignRoleAreaLocation.set(
                  `${selectedService}${roleAreaToUnassign.roleArea}${selectedLocationHashKey}${roleAreaToUnassign.level}`,
                  {
                    contact: newContactCreate,
                    service: selectedService,
                    level: roleAreaToUnassign.level,
                    roleArea: roleAreaToUnassign.roleArea,
                    location: selectedLocationHashKey,
                  },
                );
              });
            }

            const externalLevelDeassignation: ExternalLevelDeassignation = {
              externalContact: contact,
              roleAreaLocationCombo: roleAreaLocationComboToDeassignate,
            };
            const mapping = new Map<string, ExternalLevelDeassignation>();
            mapping.set(contact.hash_key, externalLevelDeassignation);
            // Make sure to remove contact types from contact in case the user closses the browser
            mutateExternalContactsUnassign(mapping);

            blockedIndexes.set(locationIndex, newContactCreate);
            setBlockedIndexes(new Map(blockedIndexes));
            setMichelinOffersPendingAssign(new Set(michelinOffersPendingAssign));
            setMandatorySecondaryConflictsPendingAssign(new Set(mandatorySecondaryConflictsPendingAssign));

            // Update for contact in onClose.
            setContactSelReaRoleLocation(new Map(contactSelectedReassignRoleAreaLocation));
            setUnassignLocalRoleLocationComboSet(unassignLocalRoleLocationComboSet);
            setCreateContactMode(false);
            // Go to the next location
            if (locationIndex + 1 < locationsHashKeys.length) setLocationIndex(locationIndex + 1);
            // Close dialog if there was only one Contact
            if (locationsHashKeys.length === 1) onClose(true);
          };

          const onAfterBackHandler = () => {
            setCreateContactMode(false);
          };

          content = (
            <ContactDetails
              action="create"
              avoidFirstTimeAssign
              contactTypes={contactTypes}
              delegateControl={contactController.current}
              disablePriorityLevels
              disableContactTypes
              disableAssignLocations
              disableContactLevel
              locations={[selectedLocation]}
              onAfterSaveHandler={onAfterSaveHandler}
              onAfterBackHandler={onAfterBackHandler}
              hideHeader
              blockReassign
            />
          );
        } else {
          content = (
            <>
              <div>
                <p>
                  <b>{customer_name}</b>
                  <br />
                  {t('Location:')}
                  {extrnl_cust_id !== null && extrnl_cust_id !== 'null' ? extrnl_cust_id : ' '}
                  <br />
                  {(customer_addr1 || customer_addr2 || '').replace(/ (null )+/g, ' ')}
                </p>
              </div>
              <Divider variant="middle" style={{ marginBottom: '1rem' }} />
              <ReassignSelectedRoleAreasTable
                disabledPanelContact={blockedIndexes.get(locationIndex)}
                contact={contact}
                service={selectedService}
                selectedRoleAreas={selectedRoleAreas}
                selectedLocation={selectedLocation}
                locationContacts={locationContacts}
                checkHandler={checkHandler}
                contactSelectedReassignRoleAreaLocation={contactSelectedReassignRoleAreaLocation}
              />
            </>
          );
        }
        secondActionButton = (
          <>
            {createContactMode && (
              <Button
                color="success"
                style={{ margin: '8px' }}
                onClick={() => {
                  if (contactController.current.save) contactController.current.save();
                }}
              >
                {t('Save')}
              </Button>
            )}
            {!createContactMode && (
              <Button
                color="primary"
                onClick={() => {
                  if (alreadyShownWarningCreate) {
                    setCreateContactMode(true);
                    return;
                  }
                  dialogs.alertDialog(
                    t(
                      // eslint-disable-next-line max-len
                      'Once the contact is created all roles and priority levels from {{contactFirstName}} {{contactLastName}} will be reassigned him/her for this location. This action cannot be undone.',
                      { contactFirstName: contact.first_name, contactLastName: contact.last_name },
                    ),
                    t('Warning'),
                    t('Ok'),
                    () => {
                      setCreateContactMode(true);
                      setAlreadyShownWarningCreate(true);
                    },
                  );
                }}
              >
                {t('Create Contact')}
              </Button>
            )}
            {!createContactMode && (
              <Button
                disabled={
                  requiredReassignments.size === 0 ||
                  Array.from(requiredReassignments).some((key) => !selectedReassignmentKeys.has(key))
                }
                color="primary"
                onClick={() => {
                  onClose(true);
                }}
              >
                {t('Save')}
              </Button>
            )}
            {!createContactMode && locationsHashKeys.length !== 1 && (
              <Button
                color="primary"
                disabled={locationIndex === 0}
                onClick={() => setLocationIndex(locationIndex + -1)}
              >
                {t('Back')}
              </Button>
            )}
            {!createContactMode && locationsHashKeys.length !== 1 && (
              <Button
                color="primary"
                disabled={locationIndex === locationsKeys.size - 1}
                onClick={() => setLocationIndex(locationIndex + 1)}
              >
                {t('Next')}
              </Button>
            )}
          </>
        );
      }
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>
        {secondActionButton}
        {!createContactMode && (
          <Button color="primary" disabled={createContactMode} onClick={() => onClose(false)}>
            {t('Cancel')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
